import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { getQueryKey } from '@trpc/react-query';

import { addSnackbarAlert } from '@sternum/ui';
import { useAfterAsyncAction } from '@sternum/web/hooks';
import { namespaces } from '@sternum/web/services/i18n';
import { trpc, TRPCReactQueryOptions } from '@sternum/web/services/trpc';
import { useAppDispatch } from '@sternum/web/store';

import { DashboardId } from '../../models';
import { setIsJustCreatedForDashboardIdAction } from '../../slice';

type UseCreateDashboardOptions = TRPCReactQueryOptions['dashboard']['createDashboard'];

export function useCreateDashboard(options: UseCreateDashboardOptions = {}) {
  const { t } = useTranslation(namespaces.dashboard);
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();
  const { execIfMounted } = useAfterAsyncAction();

  const setIsJustCreated = (dashboardId: DashboardId) => {
    dispatch(setIsJustCreatedForDashboardIdAction({ dashboardId, isJustCreated: true }));
  };

  return trpc.dashboard.createDashboard.useMutation({
    ...options,
    onSuccess(response, { title }) {
      execIfMounted(() => setIsJustCreated(response.id));
      queryClient.invalidateQueries(getQueryKey(trpc.dashboard.getDashboard));
      queryClient.invalidateQueries(getQueryKey(trpc.dashboard.getDashboards));

      addSnackbarAlert({
        variant: 'success',
        message: (
          <Trans i18nKey="createDashboardSuccess" t={t} values={{ title }} components={{ strong: <strong /> }} />
        ),
      });
    },
  });
}
